// @ts-strict-ignore
import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import Tooltip from 'antd/lib/tooltip';
import Icon, {
    BlockOutlined,
    BranchesOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    FileTextOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MobileOutlined,
    PlusOutlined,
    ProfileOutlined,
    PullRequestOutlined,
    SolutionOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';
import LinkageIcon from '../icons/LinkageIcon';
import {
    antGreen,
    antGrey,
    colorWithAlpha,
    getAvatarColorForName,
    runwayColors,
    rwColors,
} from '../styles/globalStyles';
import {
    AppPlatform,
    BaseCIIntegrationData,
    BucketMember,
    BuildDetails,
    BuildDistroArtifactDownloadStatus,
    BuildDistroBucketBaseProperties,
    BuildDistroBucketRuleType,
    BuildDistroBuild,
    BuildDistroBuildSharedWithMe,
    BuildDistroBuildStatus,
    BuildDistroMemberPermissionLevel,
    BuildDistroMemberType,
    BuildInfo,
    BuildStatus,
    IntegrationType,
    OrgUser,
    ProvisioningProfileType,
    PullRequest,
    User,
    UserGroup,
    UserGroupId,
} from '../../types';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import Space from 'antd/lib/space';
import { dayjs } from '@/extendedDayjs';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import { useDispatch, useSelector } from 'react-redux';
import {
    artifactTypeDescription,
    displayNameForAppleCertificateType,
    displayTextForBuildBucketPermission,
    displayTextForProvisioningProfileType,
    displayTextForUserGroup,
    displayUserNameLong,
    displayUserNameNoEmail,
    displayUserNameShort,
    getBucketColor,
    isDownloadOnlyBuild,
    shortCommitHashFromHash,
    statusColorForBuildStatus,
} from '../../helpers/displayHelpers';
import { useIsMobile } from '../../mediaQueriesHelpers';
import { RootState } from '../../reducers/reducers';
import {
    selectAppSettings,
    selectAppTimeFormatSetting,
    selectBuildDistributionBucketBuildsByIds,
    selectBuildDistributionBucketById,
    selectEffectiveTimezone,
    selectOrgGroups,
    selectShouldShowSimplifiedBdUIForUser,
    selectUser,
    selectUserById,
} from '../../selectors/selectors';
import { DatetimeFormat, formatDateWithTimezone } from '../../timeHelpers';
import List from 'antd/lib/list';
import {
    buildDistroBuildSort,
    buildDistroSharedBuildSort,
    getIsApplePlatform,
    isGooglePlatform,
    sortBucketMembers,
} from '../../helpers';

import Search from 'antd/lib/input/Search';
import { tryUpdateBuildDistributionBucket } from '../../actions/buildDistribution';
import { BucketEntityRequest } from '../../actions/entities';
import { BuildDistributionUpdatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import { FilterableKeysAndOptions, FilterTableButton } from '../tableFilters/FilterTableButton';
import { BucketRoleSelect } from './AddEditBucketMembers';
import { AddBucketMembersModal } from './BuildBucketDetails';
import Table, { ColumnType } from 'antd/lib/table';
import Popconfirm from 'antd/lib/popconfirm';
import { compact } from 'lodash';
import Popover from 'antd/lib/popover';
import Paragraph from 'antd/lib/typography/Paragraph';
import BuildDistroInstallMenu from './BuildDistroInstallMenu';
import { DrawerListItem } from './mobileComponents/ResponsiveDrawer';
import { AppComponentProps, WorkflowAndBranchData } from '../../typescriptHelpers';
import MarkdownComponent from '../HOCs/Markdown';
import orgOutlined from '../../../assets/organization-outlined.svg';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import IntegrationIcon from '../icons/IntegrationIcon';
import AuthedUrlButton from '../authedUrlButton/AuthedUrlButton';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { useHistory } from 'react-router-dom';
import { trackBuildDistroViewEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';

export enum SpecialBucketSection {
    SharedWithMe = 'SharedWithMe',
    Archived = 'Archived',
}

export interface BuildBucketAvatarProps {
    size: number;
    bucket: BuildDistroBucketBaseProperties;
    isSharedWithMe?: boolean;
    appImageUrl: string;
}

export const BuildBucketAvatar: React.FC<BuildBucketAvatarProps> = ({ size, bucket, isSharedWithMe, appImageUrl }) => {
    if (isSharedWithMe) {
        return <BlockOutlined style={{ color: antGrey.grey6, fontSize: size }} />;
    }

    const firstLetter = bucket.name.charAt(0).toUpperCase();
    const borderColor = getBucketColor(bucket.name, bucket.type);
    const innerBorderSize = Math.round(size * 0.2);
    const innerBorderRadius = Math.round(size * 0.1);

    const outerBorderSize = Math.round(size * 0.15);
    const outerBorderRadius = innerBorderRadius + innerBorderSize + outerBorderSize - 1; // -1 for gray border outside apps icon

    return (
        <div
            style={{
                borderRadius: outerBorderRadius,
                height: size,
                width: size,
                boxShadow: 'var(--rw-shadow-sm)',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: borderColor,
                position: 'relative',
                boxSizing: 'content-box',
                ...(borderColor && { border: `${outerBorderSize + innerBorderSize}px solid ${borderColor}` }),
            }}
        >
            <Avatar
                shape="square"
                size={size}
                src={appImageUrl}
                style={{
                    borderRadius: innerBorderRadius,
                    boxShadow: `0px 0px 0px ${innerBorderSize}px ${runwayColors.white}`,
                    ...(borderColor && { border: `1px solid ${antGrey.grey5}` }),
                }}
            >
                {firstLetter}
            </Avatar>
        </div>
    );
};

export const WorkflowTag: React.FC<{ workflowName?: string; integrationType?: IntegrationType }> = ({
    workflowName,
    integrationType,
}) => {
    if (!workflowName) {
        return null;
    }

    const [vcsIntegrationIcon, vcsIntegrationIconAlt] =
        integrationType ? iconAndAltForIntegrationType(integrationType) : [];

    return (
        <Tag
            icon={
                vcsIntegrationIcon && (
                    <img
                        src={vcsIntegrationIcon}
                        alt={vcsIntegrationIconAlt}
                        width={14}
                        height={14}
                        style={{
                            marginRight: 4,
                        }}
                    />
                )
            }
            style={{
                color: runwayColors.primary,
                backgroundColor: colorWithAlpha(rwColors.primary, 0.1),
                borderColor: 'transparent',
            }}
        >
            <Text
                style={{
                    color: antGrey.grey8,
                }}
            >
                {workflowName}
            </Text>
        </Tag>
    );
};

export const WorkflowAndBranchTag: React.FC<{
    workflowAndBranch: WorkflowAndBranchData;
    buildSystemIntegrationType?: IntegrationType;
    vcsIntegrationType?: IntegrationType;
}> = ({ workflowAndBranch, buildSystemIntegrationType, vcsIntegrationType }) => {
    const { workflowName, branch, type, vcsUsers } = workflowAndBranch;

    if (!workflowName) {
        return null;
    }

    const BranchOrPRIcon = type === BuildDistroBucketRuleType.Branch ? BranchesOutlined : PullRequestOutlined;

    const [firstVCSUser, ...otherVCSUsers] = vcsUsers;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '2px 10px',
                backgroundColor: colorWithAlpha(rwColors.primary, 0.1),
                borderRadius: 4,
                gap: 5,
            }}
        >
            {buildSystemIntegrationType && <IntegrationIcon integrationId={buildSystemIntegrationType} size={16} />}
            <Text
                style={{
                    color: antGrey.grey8,
                    fontSize: 12,
                }}
            >
                {workflowName}
            </Text>
            <PlusOutlined
                style={{
                    fontSize: 10,
                    color: runwayColors.primary,
                }}
            />
            <BranchOrPRIcon
                style={{
                    fontSize: 14,
                    color: runwayColors.primary,
                }}
            />
            <Text
                code
                style={{
                    color: antGrey.grey8,
                    borderColor: 'transparent',
                    fontSize: 12,
                }}
            >
                {branch}
            </Text>
            {firstVCSUser && (
                <>
                    <PlusOutlined
                        style={{
                            fontSize: 10,
                            color: runwayColors.primary,
                        }}
                    />
                    {vcsIntegrationType && <IntegrationIcon integrationId={vcsIntegrationType} size={16} />}
                    <Text
                        style={{
                            color: antGrey.grey8,
                            fontSize: 12,
                        }}
                    >
                        {`${firstVCSUser.username} ${otherVCSUsers.length > 0 ? `(and ${otherVCSUsers.length} more)` : ''}`}
                    </Text>
                </>
            )}
        </div>
    );
};

export const MultipleWorkflowsAndBranchesTag: React.FC<{
    workflowAndBranches: WorkflowAndBranchData[];
    buildSystemIntegrationType?: IntegrationType;
    vcsIntegrationType?: IntegrationType;
}> = ({ workflowAndBranches, buildSystemIntegrationType, vcsIntegrationType }) => {
    if (workflowAndBranches.length === 0) {
        return null;
    }

    const [firstWorkflowAndBranch, ...rest] = workflowAndBranches;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'start',
                flexWrap: 'wrap',
                padding: '10px 0',
                gap: 10,
            }}
        >
            <WorkflowAndBranchTag
                workflowAndBranch={firstWorkflowAndBranch}
                buildSystemIntegrationType={buildSystemIntegrationType}
                vcsIntegrationType={vcsIntegrationType}
            />
            {rest.length > 0 && (
                <Popover
                    content={
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 8,
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: 12,
                                    color: antGrey.grey7,
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    letterSpacing: '0.1em',
                                    paddingBottom: 8,
                                }}
                            >
                                Bucket rules
                            </Text>
                            {rest.map((workflowAndBranch) => (
                                <WorkflowAndBranchTag
                                    key={workflowAndBranch.workflowName + workflowAndBranch.branch}
                                    workflowAndBranch={workflowAndBranch}
                                    buildSystemIntegrationType={buildSystemIntegrationType}
                                    vcsIntegrationType={vcsIntegrationType}
                                />
                            ))}
                        </div>
                    }
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px 10px',
                            backgroundColor: colorWithAlpha(rwColors.primary, 0.1),
                            borderRadius: 4,
                            gap: 5,
                        }}
                    >
                        <Text
                            style={{
                                color: antGrey.grey8,
                                fontSize: 12,
                            }}
                        >
                            +{rest.length} more rule{rest.length > 1 ? 's' : ''}
                        </Text>

                        <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey }} />
                    </div>
                </Popover>
            )}
        </div>
    );
};

export const ArtifactTag: React.FC<{ artifactFileName?: string }> = ({ artifactFileName }) => {
    if (!artifactFileName) {
        return null;
    }

    return (
        <Tag
            icon={<FileTextOutlined />}
            style={{
                color: antGrey.grey8,
                backgroundColor: antGrey.grey2,
                borderColor: antGrey.grey5,
            }}
        >
            <Text
                code
                style={{
                    color: antGrey.grey8,
                    borderColor: 'transparent',
                }}
            >
                {artifactFileName}
            </Text>
        </Tag>
    );
};

export const BranchTag: React.FC<{ branch?: string }> = ({ branch }) => {
    if (!branch) {
        return null;
    }

    return (
        <Tooltip title={branch}>
            <Tag
                icon={<BranchesOutlined />}
                style={{
                    color: antGrey.grey8,
                    backgroundColor: antGrey.grey2,
                    borderColor: antGrey.grey5,
                }}
            >
                <Text
                    code
                    style={{
                        color: antGrey.grey8,
                        borderColor: 'transparent',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        maxWidth: '15vw',
                        verticalAlign: 'top',
                    }}
                >
                    {branch}
                </Text>
            </Tag>
        </Tooltip>
    );
};

export const ProfileTag: React.FC<{ profileType?: ProvisioningProfileType }> = ({ profileType }) => {
    if (!profileType) {
        return null;
    }

    return (
        <Tag
            icon={<ProfileOutlined />}
            style={{
                fontSize: 12,
                color: antGreen.green6,
                backgroundColor: antGreen.green1,
                borderColor: antGreen.green3,
            }}
        >
            {displayTextForProvisioningProfileType(profileType)}
        </Tag>
    );
};

export const BuildNumberTagBase: React.FC<{
    ciBuildIdentifier?: string;
    ciIntegrationId?: IntegrationType;
    ciBuildStatus?: BuildStatus;
    binaryBuildIdentifier?: string;
    binaryBuildVersion?: string;
    backgroundColor?: string;
    fontSize?: number;
}> = ({
    ciBuildIdentifier,
    ciIntegrationId,
    ciBuildStatus,
    binaryBuildIdentifier,
    binaryBuildVersion,
    backgroundColor,
    fontSize,
}) => {
    const [integrationIcon, integrationIconAlt] = ciIntegrationId ? iconAndAltForIntegrationType(ciIntegrationId) : [];

    const textStyle = {
        color: antGrey.grey8,
        fontWeight: 400,
        flexShrink: 0,
        fontSize: fontSize ?? 14,
    };

    return (
        <div
            style={{
                padding: '4px 8px',
                borderRadius: 8,
                backgroundColor: backgroundColor ?? antGrey.grey3,
                flexShrink: 0,
            }}
        >
            <div style={{ gap: 8, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {ciBuildStatus && <Badge dot color={statusColorForBuildStatus(ciBuildStatus)} />}
                {ciIntegrationId && ciBuildIdentifier && (
                    <div style={{ gap: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {integrationIcon && (
                            <img
                                src={integrationIcon}
                                alt={integrationIconAlt}
                                style={{ height: 16, width: 16, color: antGrey.grey7 }}
                            />
                        )}
                        <Text style={textStyle}>#{ciBuildIdentifier}</Text>
                    </div>
                )}
                {ciBuildIdentifier && binaryBuildIdentifier && <LinkageIcon width={16} height={16} />}
                {binaryBuildIdentifier && (
                    <div style={{ gap: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <MobileOutlined
                            style={{
                                color: antGrey.grey6,
                            }}
                        />
                        <Text style={textStyle}>
                            #{binaryBuildIdentifier}
                            {binaryBuildVersion ? ` (${binaryBuildVersion})` : ''}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};

export const BuildNumberTag: React.FC<{
    build: BuildDistroBuild;
    backgroundColor?: string;
    fontSize?: number;
    showBadge?: boolean;
}> = ({ build, backgroundColor, fontSize, showBadge }) => {
    if (!build) {
        return null;
    }

    return (
        <BuildNumberTagBase
            ciBuildIdentifier={build.ciBuild?.buildIdentifier}
            ciIntegrationId={build.ciBuild?.integrationId}
            ciBuildStatus={build.ciBuild && showBadge ? build.ciBuild.buildStatus : null}
            binaryBuildIdentifier={build.binaryBuild?.buildNumber}
            binaryBuildVersion={build.binaryBuild?.buildVersion}
            backgroundColor={backgroundColor}
            fontSize={fontSize}
        />
    );
};

export interface BuildRowProps {
    build: BuildDistroBuild;
    shouldShowBucketType?: boolean;
    appId: string;
    orgId: string;
    showSharedInfo?: boolean;
    shouldHideBranch?: boolean;
}

export const BuildTypeAndDate = ({
    uploadedAt,
    showSharedInfoWithBuild,
    ciBuild,
    appId,
}: {
    uploadedAt: string;
    showSharedInfoWithBuild?: BuildDistroBuildSharedWithMe;
    ciBuild?: BuildInfo;
    appId?: string;
}) => {
    const isMobile = useIsMobile();
    const use24HourTimeFormat = useSelector((state: RootState) =>
        appId ? selectAppTimeFormatSetting(state, appId) : false,
    );
    const effectiveTimezone = useSelector((state: RootState) =>
        appId ? selectEffectiveTimezone(state, appId) : undefined,
    );

    const date = showSharedInfoWithBuild?.sharedOn ?? uploadedAt;
    const action =
        showSharedInfoWithBuild?.sharedOn ? 'Shared'
        : ciBuild ? 'Built'
        : 'Uploaded';
    const description =
        showSharedInfoWithBuild?.sharedOn ? 'The time at which the build artifact was shared with you.'
        : ciBuild ? 'The time at which the artifact started building in the CI/CD workflow.'
        : 'The time at which the build artifact was uploaded to Runway.';

    const dayjsDate = dayjs(date);
    const shouldDisplayShortDate = dayjsDate.isSame(dayjs(), 'year');
    const localTimezone = dayjs.tz.guess();
    const isLocalTimezone = effectiveTimezone === localTimezone;
    const shortTz =
        effectiveTimezone && !isLocalTimezone ? ` ${dayjs.tz(dayjsDate, effectiveTimezone).format('z')}` : '';

    const tooltipFormattedDate = formatDateWithTimezone(
        date,
        shouldDisplayShortDate ? 'short' : 'full',
        effectiveTimezone,
        use24HourTimeFormat,
    );
    const formattedDate =
        isMobile ?
            formatDateWithTimezone(date, 'mobile', effectiveTimezone, use24HourTimeFormat)
        :   tooltipFormattedDate;

    return (
        <Tooltip
            title={
                <div
                    style={{
                        whiteSpace: 'pre-line',
                    }}
                >
                    {`${action} on ${tooltipFormattedDate}${shortTz}

                        ${description}`}
                </div>
            }
        >
            <Space align="center">
                <Text
                    style={{
                        fontSize: isMobile ? 12 : 14,
                        color: antGrey.grey8,
                        fontWeight: 600,
                    }}
                    ellipsis
                >
                    {' '}
                    {ciBuild?.buildStatus === BuildStatus.InProgress ? 'In Progress' : `${formattedDate}${shortTz}`}
                </Text>
            </Space>
        </Tooltip>
    );
};

interface MobileBuildRowProps extends AppComponentProps {
    build: BuildDistroBuild;
    shouldShowBucketType: boolean;
    onBuildClick?: (buildId: string) => void;
    showSharedInfo?: boolean;
}

export const BuildRowMobile: React.FC<MobileBuildRowProps> = ({
    build,
    shouldShowBucketType,
    onBuildClick,
    appId,
    showSharedInfo,
}) => {
    // TODO: refactor so we're not calling useSelector for each and every row : (
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);
    const bucket = useSelector((state: RootState) => selectBuildDistributionBucketById(state, build?.bucketId));
    let sharedBuild;
    if (showSharedInfo) {
        sharedBuild = build as BuildDistroBuildSharedWithMe;
    }
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'start',
                justifyContent: 'space-between',
                width: '100%',
                cursor: 'pointer',
                flex: 1,
            }}
            onClick={() => onBuildClick && onBuildClick(build.id)}
        >
            <Space size={12} align="center">
                {shouldShowBucketType && (
                    <BuildBucketAvatar appImageUrl={appImageUrl} size={32} bucket={bucket} isSharedWithMe={!bucket} />
                )}
                <BuildTypeAndDate
                    uploadedAt={build.uploadedAt}
                    showSharedInfoWithBuild={sharedBuild}
                    ciBuild={build.ciBuild}
                    appId={appId}
                />
            </Space>
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <BuildNumberTag build={build} fontSize={16} showBadge />
                {!bucket?.publicBucketLinkEnabled && build.publicBuildLinkEnabled && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                        }}
                    >
                        <BucketAccessIconTooltip isExternalBuildView />
                    </div>
                )}
            </Space>
        </div>
    );
};

export const UsernameAvatar: React.FC<{ name: string }> = ({ name }) => {
    return (
        <Avatar
            style={{
                fontSize: 12,
                backgroundColor: getAvatarColorForName(name),
                flexShrink: 0,
                margin: 4,
            }}
            size={24}
        >
            {name.charAt(0).toUpperCase()}
        </Avatar>
    );
};

export const AppAndOrgAvatar: React.FC<{ name: string; url?: string }> = ({ name, url }) => {
    return (
        <Avatar style={{ marginRight: 16, flexShrink: 0 }} size={48} src={url}>
            {name.charAt(0).toUpperCase()}
        </Avatar>
    );
};

const artifactErrorText = (build?: BuildDistroBuild | BuildDetails) => {
    if (!build) {
        return null;
    }

    if (isDownloadOnlyBuild(build)) {
        return null;
    }

    const artifactNotFoundText = (
        <Text ellipsis style={{ color: runwayColors.statusRed }}>
            Artifact not found
        </Text>
    );
    const artifactInfo = build.artifactInfo;

    if (artifactInfo?.downloadStatus === BuildDistroArtifactDownloadStatus.InProgress) {
        const inProgressText = <Text style={{ color: runwayColors.statusYellow }}>Trying to resolve artifacts...</Text>;
        return artifactInfo.errorMessage ?
                <Tooltip title={`Last attempt failed: ${artifactInfo.errorMessage}`}>
                    <Space size={4} align="center">
                        {inProgressText}
                        <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey }} />
                    </Space>
                </Tooltip>
            :   inProgressText;
    }

    return (
        ((!build.binaryBuild && build.ciBuild?.buildStatus !== BuildStatus.InProgress) ||
            artifactInfo?.downloadStatus === BuildDistroArtifactDownloadStatus.Failed) && (
            <Space align="center">
                <Icon component={CloseCircleOutlined} style={{ color: runwayColors.statusRed }} />
                {build.artifactInfo?.errorMessage ?
                    <Tooltip title={build.artifactInfo.errorMessage}>
                        <Space size={4} align="center" style={{ cursor: 'help' }}>
                            {artifactNotFoundText}
                            <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey }} />
                        </Space>
                    </Tooltip>
                :   artifactNotFoundText}
            </Space>
        )
    );
};

export const BuildRow: React.FC<BuildRowProps> = ({
    build,
    shouldShowBucketType,
    appId,
    orgId,
    showSharedInfo,
    shouldHideBranch,
}) => {
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);

    // TODO: can we lift this up? it's a bit dangerous to have here for performance but also because buckets are not always present when this component is rendered
    const bucket = useSelector((state: RootState) => selectBuildDistributionBucketById(state, build?.bucketId));
    const simplifiedBuild = useSelector((state: RootState) => selectShouldShowSimplifiedBdUIForUser(state));
    const sharedBuild = build as BuildDistroBuildSharedWithMe;

    const sharedByUser = useSelector((state: RootState) =>
        sharedBuild?.sharedBy ? selectUserById(state, orgId, appId, sharedBuild.sharedBy) : null,
    );

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    if (!build) {
        return null;
    }

    const renderSharedByIcon = () => {
        if (!sharedByUser) {
            return null;
        }

        const displayUserName = displayUserNameShort(sharedByUser);

        const avatar = <UsernameAvatar name={displayUserName} />;

        const textStyle = {
            color: antGrey.grey5,
        };

        const rowDivStyle = {
            display: 'flex',
            gap: 8,
            justifyContent: 'space-between',
        };

        const popoverContent = (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: '2px',
                }}
            >
                <div style={rowDivStyle}>
                    <Text style={textStyle}>Shared by</Text>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {avatar}
                        <Text style={textStyle}>{displayUserNameLong(sharedByUser)}</Text>
                    </div>
                </div>
                <div style={rowDivStyle}>
                    <Text style={textStyle}>Shared date</Text>
                    <Text style={textStyle}>
                        {formatDateWithTimezone(
                            sharedBuild.sharedOn,
                            'shortWithComma',
                            effectiveTimezone,
                            use24HourTimeFormat,
                        )}
                    </Text>
                </div>
            </div>
        );

        return (
            <Tooltip title={popoverContent} overlayStyle={{ minWidth: 280, maxWidth: 600 }}>
                <>{avatar}</>
            </Tooltip>
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Space
                size={24}
                style={{
                    cursor: 'pointer',
                    flex: 1,
                }}
                align="center"
                wrap
            >
                <Space style={{ width: shouldShowBucketType ? 220 : 170 }} size={16} align="center">
                    {shouldShowBucketType && (
                        <BuildBucketAvatar
                            appImageUrl={appImageUrl}
                            size={32}
                            bucket={bucket}
                            isSharedWithMe={!bucket}
                        />
                    )}
                    <BuildTypeAndDate
                        uploadedAt={build.uploadedAt}
                        showSharedInfoWithBuild={sharedBuild}
                        ciBuild={build.ciBuild}
                        appId={appId}
                    />
                </Space>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <BuildNumberTag build={build} fontSize={15} showBadge />
                    {!simplifiedBuild && (
                        <>
                            {build.prData ?
                                <PRDetailsPopover prData={build.prData} />
                            :   build.ciBuild && <CommitDetailsPopover ciBuild={build.ciBuild} />}
                            {build.binaryBuild ?
                                <ArtifactTag artifactFileName={build.binaryBuild.artifactFileName}></ArtifactTag>
                            :   isDownloadOnlyBuild(build) && (
                                    <ArtifactTag artifactFileName={build.originalFileName}></ArtifactTag>
                                )
                            }
                            {!shouldHideBranch &&
                                build.ciBuild?.branchName &&
                                bucket?.rules.length === 1 &&
                                bucket?.rules[0].isTokenizedBranch === true && (
                                    <BranchTag branch={build.ciBuild.branchName} />
                                )}
                            {artifactErrorText(build)}
                        </>
                    )}
                </div>
            </Space>
            <Space
                align="center"
                onClick={(e) => {
                    e.stopPropagation();
                }}
                size={4}
            >
                {!bucket?.publicBucketLinkEnabled && build.publicBuildLinkEnabled && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                        }}
                    >
                        <BucketAccessIconTooltip isExternalBuildView />
                    </div>
                )}
                {showSharedInfo && renderSharedByIcon()}
                <Popover
                    showArrow={false}
                    placement="right"
                    content={
                        <div
                            style={{
                                width: 210,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 8,
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: 12,
                                    color: antGrey.grey7,
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    letterSpacing: '0.1em',
                                    paddingBottom: 8,
                                }}
                            >
                                Tester notes
                            </Text>
                            {build.testerNotes ?
                                <MarkdownComponent
                                    source={build.testerNotes}
                                    style={{
                                        fontSize: 12,
                                    }}
                                    className="markdown-preview-with-breaks"
                                />
                            :   <Text
                                    style={{
                                        fontSize: 12,
                                        color: antGrey.grey6,
                                        fontWeight: 400,
                                    }}
                                >
                                    No notes
                                </Text>
                            }
                        </div>
                    }
                >
                    <Button icon={<SolutionOutlined />} type="link" />
                </Popover>
                <BuildDistroInstallMenu
                    orgId={orgId}
                    appId={appId}
                    buildDistroBuild={build}
                    shouldShowInfoIcon={false}
                    customTooltipPlacement="left"
                    bucketId={bucket?.id}
                >
                    {(isDisabled, isLoading) => (
                        <Button
                            icon={
                                build.status === BuildDistroBuildStatus.Expired ?
                                    <CloseCircleOutlined />
                                :   <MobileOutlined />
                            }
                            type="link"
                            loading={isLoading}
                            disabled={isDisabled}
                        />
                    )}
                </BuildDistroInstallMenu>
            </Space>
        </div>
    );
};

export const PRDetailsPopover: React.FC<{ prData: PullRequest }> = ({ prData }) => {
    if (!prData) {
        return null;
    }

    return (
        <Popover content={<PRDetails prData={prData} />}>
            <Space align="center">
                <CommitOrPRAuthorAvatar author={prData.owner} />
                {prData.number && (
                    <Text
                        style={{
                            fontSize: 14,
                            color: antGrey.grey7,
                        }}
                        ellipsis
                    >
                        {`PR #${prData.number}${prData.title ? ': ' : ''}`}
                    </Text>
                )}
                {prData.title && (
                    <Text
                        style={{
                            fontSize: 14,
                            color: antGrey.grey7,
                            width: 215,
                        }}
                        ellipsis
                    >
                        {prData.title}
                    </Text>
                )}
            </Space>
        </Popover>
    );
};

export const CommitDetailsPopover: React.FC<{ ciBuild: BuildInfo }> = ({ ciBuild }) => {
    if (!ciBuild) {
        return null;
    }

    const missingCommitInfo = !ciBuild.commitAuthor && !ciBuild.commitMessage;

    return (
        <Popover content={<CommitDetails ciBuild={ciBuild} />}>
            {missingCommitInfo ?
                <div>
                    <CommitHash commitHash={ciBuild.commit} commitUrl={ciBuild.commitUrl} emphasizeForHover />
                </div>
            :   <Space align="center">
                    <CommitOrPRAuthorAvatar author={ciBuild.commitAuthor} />
                    <Text
                        style={{
                            fontSize: 14,
                            color: antGrey.grey7,
                            maxWidth: 215,
                        }}
                        ellipsis
                    >
                        {ciBuild.commitMessage ?? 'No commit message found'}
                    </Text>
                </Space>
            }
        </Popover>
    );
};

const PRDetails: React.FC<{ prData: PullRequest }> = ({ prData }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Text
                style={{
                    fontSize: 12,
                    color: antGrey.grey7,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                }}
            >
                PULL REQUEST INFO
            </Text>

            {prData.number && (
                <Link
                    style={{ fontSize: 16 }}
                    href={prData.url}
                    target="_blank"
                    ellipsis
                    onClick={(e) => e.stopPropagation()}
                >
                    {`PR #${prData.number}`}
                </Link>
            )}
            <Space>
                <CommitOrPRAuthorAvatar author={prData.owner} />
                <Text
                    style={{
                        fontSize: 14,
                        color: antGrey.grey7,
                    }}
                    ellipsis
                >
                    {prData.owner}
                </Text>
            </Space>
            {prData.title && (
                <Text
                    style={{
                        fontSize: 14,
                        color: antGrey.grey7,
                    }}
                    ellipsis
                >
                    {prData.title}
                </Text>
            )}
        </div>
    );
};

const CommitDetails: React.FC<{ ciBuild: BuildInfo }> = ({ ciBuild }) => {
    const missingCommitInfo = !ciBuild.commitAuthor && !ciBuild.commitMessage;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Text
                style={{
                    fontSize: 12,
                    color: antGrey.grey7,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                }}
            >
                COMMIT INFO
            </Text>
            <CommitHash commitHash={ciBuild.commit} commitUrl={ciBuild.commitUrl} />
            {ciBuild.commitAuthor && <CommitAuthorAvatarAndName commitAuthor={ciBuild.commitAuthor} />}
            {ciBuild.commitMessage && (
                <Paragraph
                    style={{
                        fontSize: 14,
                        color: antGrey.grey7,
                        maxWidth: 215,
                    }}
                    ellipsis={{ rows: 4, expandable: true, symbol: 'more' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {ciBuild.commitMessage}
                </Paragraph>
            )}
            {missingCommitInfo && <Text>No commit info found</Text>}
        </div>
    );
};

export const SharedWithMeBuildsList: React.FC<BucketBuildsListParams> = (props: BucketBuildsListParams) => {
    return <BucketBuildsList {...props} sortBuilds={buildDistroSharedBuildSort} showSharedInfo />;
};

export interface BucketBuildsListParams {
    appId: string;
    orgId: string;
    buildIds: string[];
    isMobile?: boolean;
    isLoading?: boolean;
    shouldShowBucketType?: boolean;
    destinationURLFactory: (buildId: string, bucketId?: string) => string;
    onBuildSelection?: (buildId: string) => void;
    showSharedInfo?: boolean;
    onLoadMore?: () => void;
    sortBuilds?: (a: BuildDistroBuild, b: BuildDistroBuild) => number;
    showLoadMore?: boolean;
    leftInset?: boolean;
}

export const BucketBuildsList: React.FC<BucketBuildsListParams> = ({
    appId,
    orgId,
    buildIds,
    isMobile,
    isLoading,
    shouldShowBucketType,
    showSharedInfo,
    destinationURLFactory,
    onLoadMore,
    sortBuilds,
    showLoadMore,
    leftInset,
    onBuildSelection,
}) => {
    const allBuilds = useSelector((state: RootState) =>
        selectBuildDistributionBucketBuildsByIds(state, buildIds ?? []),
    ).sort((a, b) => (typeof sortBuilds === 'function' ? sortBuilds(a, b) : buildDistroBuildSort(a, b)));
    const history = useHistory();

    return (
        <List
            loadMore={
                onLoadMore &&
                showLoadMore && (
                    <div
                        style={{
                            textAlign: 'center',
                            marginTop: 12,
                            height: 32,
                            lineHeight: '32px',
                        }}
                    >
                        <Button type="link" onClick={onLoadMore}>
                            Load more...
                        </Button>
                    </div>
                )
            }
            style={{
                marginLeft: isMobile && shouldShowBucketType ? 12 : 0,
                marginRight: isMobile && shouldShowBucketType ? 12 : 0,
            }}
            locale={{
                emptyText: 'No builds yet.',
            }}
            loading={isLoading}
            dataSource={allBuilds}
            split
            rowKey={(build) => (build ? build.id : null)}
            renderItem={(build) =>
                build ?
                    <List.Item
                        className="build-row"
                        onClick={() => {
                            onBuildSelection && onBuildSelection(build.id);
                            const url = destinationURLFactory(build.id, build.bucketId);
                            history.push(url);
                        }}
                        style={{ paddingLeft: leftInset && !isMobile ? 34 : 4, paddingRight: 4 }}
                    >
                        {isMobile ?
                            <BuildRowMobile
                                appId={appId}
                                orgId={orgId}
                                build={build}
                                shouldShowBucketType={shouldShowBucketType}
                                showSharedInfo={showSharedInfo}
                            />
                        :   <BuildRow
                                build={build}
                                appId={appId}
                                orgId={orgId}
                                shouldShowBucketType={shouldShowBucketType}
                                showSharedInfo={showSharedInfo}
                            />
                        }
                    </List.Item>
                :   <></>
            }
        />
    );
};

export const BuildsList: React.FC<{
    appId: string;
    orgId: string;
    builds: BuildDistroBuild[];
    isMobile?: boolean;
    isLoading?: boolean;
    shouldShowBucketType?: boolean;
    shouldHideBranch?: boolean;
    setSelectedBuildId: (buildId: string) => void;
}> = ({ appId, orgId, builds, isMobile, isLoading, shouldShowBucketType, shouldHideBranch, setSelectedBuildId }) => {
    return (
        <List
            style={{
                marginLeft: isMobile ? 12 : 0,
            }}
            locale={{
                emptyText: 'No builds yet.',
            }}
            loading={isLoading}
            dataSource={builds}
            split
            renderItem={(build) => (
                <List.Item
                    className="build-row"
                    onClick={() => {
                        setSelectedBuildId(build.id);
                    }}
                    style={{
                        padding: '12px 6px',
                    }}
                >
                    {isMobile ?
                        <BuildRowMobile
                            appId={appId}
                            orgId={orgId}
                            build={build}
                            shouldShowBucketType={shouldShowBucketType}
                        />
                    :   <BuildRow
                            build={build}
                            appId={appId}
                            orgId={orgId}
                            shouldShowBucketType={shouldShowBucketType}
                            shouldHideBranch={shouldHideBranch}
                        />
                    }
                </List.Item>
            )}
        />
    );
};

export interface MemberDetails {
    id: string;
    title: string;
    subtitle?: string;
    imageUrl?: string;
    type: BuildDistroMemberType;
    permissions: BuildDistroMemberPermissionLevel;
}

export const mapMemberToMemberDetails = (
    member: BucketMember,
    appUsersById: Record<string, User>,
    orgGroupsById: Record<UserGroupId, UserGroup>,
    shouldShowEmail?: boolean,
): MemberDetails => {
    let title = '';
    let subtitle = null;
    let imageUrl = null;

    if (member.type === BuildDistroMemberType.User) {
        const user = appUsersById[member.id];

        if (!user) {
            return null;
        }

        if (user.commsAvatars?.image32) {
            imageUrl = user.commsAvatars.image32;
        }

        const userName = displayUserNameNoEmail(user);
        if (userName) {
            title = userName;
            if (shouldShowEmail) {
                subtitle = user.email;
            }
        } else {
            title = user.email;
        }
    } else if (member.type === BuildDistroMemberType.Group) {
        const group = orgGroupsById[member.id];
        title = displayTextForUserGroup(group);
    }

    return {
        id: member.id,
        title,
        subtitle,
        imageUrl,
        type: member.type,
        permissions: member.permissionLevel,
    };
};

export const displayableNameByWorkflowId = (baseCIData: BaseCIIntegrationData, workflowId: string): string | null => {
    if (!baseCIData) {
        return null;
    }

    if (baseCIData.workflowId === workflowId) {
        return baseCIData.workflowName;
    } else if (baseCIData.devWorkflowId === workflowId) {
        return baseCIData.devWorkflowName;
    } else if (baseCIData.prodWorkflowId === workflowId) {
        return baseCIData.prodWorkflowName;
    }

    return null;
};

export const CommitAuthorAvatarAndName = ({ commitAuthor }: { commitAuthor: string }) => {
    if (!commitAuthor) {
        return null;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CommitOrPRAuthorAvatar author={commitAuthor} />
            <Text style={{ color: antGrey.grey8 }}>{commitAuthor}</Text>
        </div>
    );
};

const CommitOrPRAuthorAvatar: React.FC<{ author?: string }> = ({ author }) => {
    return (
        <Avatar
            style={{
                fontSize: 12,
                backgroundColor: author ? getAvatarColorForName(author) : antGrey.grey5,
                flexShrink: 0,
            }}
            size={24}
            icon={!author && <UserOutlined />}
        >
            {author && author.charAt(0).toUpperCase()}
        </Avatar>
    );
};

export const CommitHash = ({
    commitHash,
    commitUrl,
    emphasizeForHover,
}: {
    commitHash?: string;
    commitUrl?: string;
    emphasizeForHover?: boolean;
}) => {
    if (!commitHash) {
        return null;
    }

    return commitUrl ?
            <Link
                style={{ fontSize: 16, fontFamily: 'monospace' }}
                href={commitUrl}
                target="_blank"
                ellipsis
                onClick={(e) => e.stopPropagation()}
            >
                {shortCommitHashFromHash(commitHash)}
            </Link>
        :   <Text
                style={{
                    fontSize: 16,
                    fontFamily: 'monospace',
                    color: emphasizeForHover ? runwayColors.primary : antGrey.grey8,
                }}
                ellipsis
            >
                {shortCommitHashFromHash(commitHash)}
            </Text>;
};

export interface ListDataSourceType {
    title: string;
    description: React.ReactNode;
}

export const BuildDistroMemberRow: React.FC<{
    member: MemberDetails;
    currentUser: User;
}> = ({ member, currentUser }) => {
    let icon: React.ReactNode = null;
    if (member.type === BuildDistroMemberType.Group) {
        icon = (
            <TeamOutlined
                style={{
                    fontSize: 18,
                    color: antGrey.grey5,
                }}
            />
        );
    }

    const isCurrentUser = member.type === BuildDistroMemberType.User && member.id === currentUser.id;

    if (member.type === BuildDistroMemberType.User) {
        icon = (
            <Avatar
                style={{
                    fontSize: 12,
                    border: 'none',
                    backgroundColor: getAvatarColorForName(member.title),
                    flexShrink: 0,
                }}
                size={25}
                src={member.imageUrl}
            >
                {member.title.charAt(0).toUpperCase()}
            </Avatar>
        );
    }

    return (
        <Space size={10}>
            {icon}
            <Text
                style={{
                    color: antGrey.grey8,
                    fontSize: 14,
                }}
            >
                {member.title}
                {isCurrentUser && ' (you)'}
            </Text>
            {member.subtitle && (
                <Text
                    style={{
                        color: antGrey.grey6,
                        fontSize: 14,
                    }}
                >
                    {member.subtitle}
                </Text>
            )}
        </Space>
    );
};

export const buildDrawerMembersListItems = (
    members: BucketMember[],
    appUsersById: Record<string, User>,
    orgGroupsById: Record<UserGroupId, UserGroup>,
    currentUser: User,
) => {
    const memberDetails = compact(
        [...members].sort(sortBucketMembers).map((member) => {
            return mapMemberToMemberDetails(member, appUsersById, orgGroupsById);
        }),
    );
    const items: DrawerListItem[] = memberDetails.map((member: MemberDetails) => {
        const item = <BuildDistroMemberRow member={member} currentUser={currentUser} />;
        return {
            title: item,
            description: (
                <Text style={{ color: antGrey.grey7 }}>{displayTextForBuildBucketPermission(member.permissions)}</Text>
            ),
        };
    });

    return items;
};

export const buildListDataSource = (
    appId: string,
    orgId: string,
    buildDetails: BuildDetails,
    appPlatform: AppPlatform,
    effectiveTimezone: string,
    sharedByUser?: OrgUser,
) => {
    const firstGroup: ListDataSourceType[] = [];
    const secondGroup: ListDataSourceType[] = [];
    const hasAssociatedCIBuild = !!buildDetails.ciBuild;
    const isApplePlatform = getIsApplePlatform(appPlatform);
    const sharedBuild = buildDetails as BuildDistroBuildSharedWithMe;
    const prData = buildDetails.prData;
    const dispatch = useDispatch();

    const uploadDate =
        buildDetails?.ciBuild ? buildDetails?.ciBuild?.buildFinishedAt
        : buildDetails?.binaryBuild ? buildDetails?.binaryBuild.uploadedAt
        : null;

    if (sharedByUser) {
        const displayUserName = displayUserNameShort(sharedByUser);

        firstGroup.push({
            title: 'Shared by',
            description: (
                <Space size={10}>
                    <UsernameAvatar name={displayUserName} />
                    <Text style={{ color: antGrey.grey8 }}>{displayUserName}</Text>
                </Space>
            ),
        });
    }

    if (sharedByUser && sharedBuild?.sharedOn) {
        firstGroup.push({
            title: 'Shared on',
            description: dayjs(sharedBuild.sharedOn).tz(effectiveTimezone).format(DatetimeFormat.ShortMonthDateTime),
        });
    }

    if (uploadDate) {
        firstGroup.push({
            title: hasAssociatedCIBuild ? 'Build finished date' : 'Upload date',
            description: dayjs(uploadDate).tz(effectiveTimezone).format(DatetimeFormat.ShortMonthDateTime),
        });
    }

    if (buildDetails.binaryBuild) {
        secondGroup.push({
            title: isGooglePlatform(appPlatform) ? 'Version code' : 'Build number',
            description: <Text>#{buildDetails.binaryBuild.buildNumber}</Text>,
        });

        secondGroup.push({
            title: isGooglePlatform(appPlatform) ? 'Version name' : 'Version string',
            description: <Text>{buildDetails.binaryBuild.buildVersion}</Text>,
        });

        if (isApplePlatform) {
            const provProfile = buildDetails?.binaryBuild?.provisioningProfile;

            if (provProfile) {
                secondGroup.push({
                    title: 'Profile',
                    description: (
                        <ProfileTag profileType={buildDetails.binaryBuild?.provisioningProfile?.profileType} />
                    ),
                });
            }

            if (buildDetails.binaryBuild?.provisioningProfile?.certificates.length > 0) {
                const certificate = buildDetails.binaryBuild?.provisioningProfile?.certificates[0];
                secondGroup.push({
                    title: 'Certificate',
                    description: <Text>{displayNameForAppleCertificateType(certificate.certificateType)}</Text>,
                });
            }
        }

        secondGroup.push({
            title: 'Build type',
            description: <Text>{artifactTypeDescription(buildDetails.binaryBuild.buildType) ?? 'Unknown'}</Text>,
        });
    }

    if (hasAssociatedCIBuild) {
        const ciBuild = buildDetails.ciBuild;
        firstGroup.push({
            title: 'Workflow',
            description: <WorkflowTag workflowName={ciBuild.workflowName} integrationType={ciBuild.integrationId} />,
        });
        firstGroup.push({
            title: 'Branch',
            description: <BranchTag branch={ciBuild.branchName} />,
        });
        firstGroup.push({
            title: `${displayNameForIntegrationType(ciBuild.integrationId)} build`,
            description:
                ciBuild.buildUrl ?
                    <Link href={ciBuild.buildUrl} target="_blank">
                        Build #{ciBuild.buildIdentifier}
                    </Link>
                :   <Text>Build #{ciBuild.buildIdentifier}</Text>,
        });
    }

    if (hasAssociatedCIBuild && prData) {
        prData.number &&
            firstGroup.push({
                title: 'Pull request',
                description: (
                    <Link
                        style={{ fontSize: 16 }}
                        href={prData.url}
                        target="_blank"
                        ellipsis
                        onClick={(e) => e.stopPropagation()}
                    >
                        {`#${prData.number}`}
                    </Link>
                ),
            });
        firstGroup.push({
            title: 'PR author',
            description: <CommitAuthorAvatarAndName commitAuthor={prData.owner} />,
        });
        firstGroup.push({
            title: 'PR title',
            description: prData.title,
        });

        firstGroup.push({
            title: 'Base branch',
            description: prData.baseBranch,
        });
    }

    const ciBuildInProgress = buildDetails.ciBuild?.buildStatus === BuildStatus.InProgress;
    if (!ciBuildInProgress) {
        let description;
        const artifactFileName = buildDetails.binaryBuild?.artifactFileName ?? buildDetails.originalFileName;
        if (artifactFileName) {
            if (buildDetails.downloadUrl) {
                description = (
                    <AuthedUrlButton
                        icon={<DownloadOutlined />}
                        onLinkAuthed={(authedDownloadLink) => {
                            window.open(authedDownloadLink, '_self');
                            dispatch(
                                trackBuildDistroViewEvent({
                                    orgId,
                                    appId,
                                    eventName: AnalyticEvents.Event.BuildDistroClickDownloadArtifactCTA,
                                }),
                            );
                        }}
                        type="link"
                        linkUrl={buildDetails.downloadUrl}
                        style={{ padding: 0 }}
                    >
                        {artifactFileName}
                    </AuthedUrlButton>
                );
            } else {
                description = (
                    <Text code style={{ color: runwayColors.darkTextGrey }} ellipsis>
                        {artifactFileName}
                    </Text>
                );
            }
        } else {
            description = artifactErrorText(buildDetails);
        }

        firstGroup.push({
            title: 'Artifact file name',
            description,
        });
    }

    return [firstGroup, secondGroup];
};

export const RemoveMemberButton: React.FC<BucketEntityRequest & { memberId: string; isDisabled: boolean }> = ({
    memberId,
    bucketId,
    appId,
    orgId,
    isDisabled,
}) => {
    const dispatch = useDispatch();

    const isRemovingBuildDistroMember = useIsLoading(
        BuildDistributionUpdatable.RemoveBuildDistroBucketMember,
        memberId,
    );

    const removeBuildDistroBucketMemberError = useError(
        BuildDistributionUpdatable.RemoveBuildDistroBucketMember,
        memberId,
    );

    React.useEffect(() => {
        if (removeBuildDistroBucketMemberError) {
            globalMessage.error(removeBuildDistroBucketMemberError);
        }
    }, [removeBuildDistroBucketMemberError]);

    const removeMember = () => {
        dispatch(
            tryUpdateBuildDistributionBucket(BuildDistributionUpdatable.RemoveBuildDistroBucketMember, {
                orgId,
                appId,
                bucketId,
                memberId,
            }),
        );
    };

    return (
        <Popconfirm
            title="Are you sure you want to remove this member?"
            onConfirm={() => removeMember()}
            okButtonProps={{ danger: true }}
            okText="Remove"
            placement="right"
            disabled={isDisabled}
        >
            <Button
                disabled={isDisabled}
                loading={isRemovingBuildDistroMember}
                style={{
                    padding: 0,
                    margin: 0,
                    height: 22,
                }}
                icon={<DeleteOutlined />}
                type="link"
                danger
            />
        </Popconfirm>
    );
};

export const EditMemberRoleSelect: React.FC<
    BucketEntityRequest & {
        memberId: string;
        isDisabled: boolean;
        defaultPermissionLevel?: BuildDistroMemberPermissionLevel;
    }
> = ({ memberId, bucketId, appId, orgId, defaultPermissionLevel, isDisabled }) => {
    const dispatch = useDispatch();

    const isUpdatingPermissions = useIsLoading(
        BuildDistributionUpdatable.UpdateBuildDistroBucketMemberPermissions,
        memberId,
    );

    const removeBuildDistroBucketMemberError = useError(
        BuildDistributionUpdatable.UpdateBuildDistroBucketMemberPermissions,
        memberId,
    );

    const removeBuildDistroBucketMemberSuccess = useUpdated(
        BuildDistributionUpdatable.UpdateBuildDistroBucketMemberPermissions,
        memberId,
    );

    React.useEffect(() => {
        if (removeBuildDistroBucketMemberError) {
            globalMessage.error(removeBuildDistroBucketMemberError);
        }
    }, [removeBuildDistroBucketMemberError]);

    React.useEffect(() => {
        if (removeBuildDistroBucketMemberSuccess) {
            globalMessage.success('Member permissions updated successfully!');
        }
    }, [removeBuildDistroBucketMemberSuccess]);

    return (
        <BucketRoleSelect
            isDisabled={isDisabled || isUpdatingPermissions}
            defaultPermissionLevel={defaultPermissionLevel}
            isLoading={isUpdatingPermissions}
            onSelect={(permissionLevel) => {
                dispatch(
                    tryUpdateBuildDistributionBucket(
                        BuildDistributionUpdatable.UpdateBuildDistroBucketMemberPermissions,
                        {
                            orgId,
                            appId,
                            bucketId,
                            memberId,
                            permissionLevel: permissionLevel as BuildDistroMemberPermissionLevel,
                        },
                    ),
                );
            }}
        />
    );
};

export interface MembersTableProps {
    members: BucketMember[];
    appId: string;
    orgId: string;
    bucketId: string;
}

const FILTERABLE_KEYS_AND_OPTIONS: FilterableKeysAndOptions<BucketMember> = {
    permissionLevel: {
        displayName: 'Permissions',
        displayNameForPossibleValue: (value) => {
            return displayTextForBuildBucketPermission(value as BuildDistroMemberPermissionLevel);
        },
        defaultValues: Object.values(BuildDistroMemberPermissionLevel),
    },
};

export const MembersTable: React.FC<MembersTableProps> = ({ members, appId, orgId, bucketId }) => {
    const [searchText, setSearchText] = React.useState('');
    const [isInEditMode, setIsInEditMode] = React.useState(false);

    const [isAddBucketMembersModalVisible, setIsAddBucketMembersModalVisible] = React.useState(false);

    const appUsersById = useSelector((state: RootState) => {
        const appUsers = selectAppOrgUsers(state, appId);

        const appUsersById = {};
        appUsers.forEach((appUser) => {
            appUsersById[appUser.id] = appUser;
        });

        return appUsersById;
    });

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, orgId));

    const [filteredMembers, setFilteredMembers] = React.useState<BucketMember[]>(members);

    const searchedMemberDetails = React.useMemo(() => {
        const memberDetails = compact(
            [...filteredMembers].sort(sortBucketMembers).map((member) => {
                return mapMemberToMemberDetails(member, appUsersById, orgGroupsById, true);
            }),
        );

        return memberDetails.filter((member) => {
            return (
                member.title.toLowerCase().includes(searchText.toLowerCase()) ||
                (!!member.subtitle && member.subtitle.toLowerCase().includes(searchText.toLowerCase()))
            );
        });
    }, [filteredMembers, appUsersById, searchText]);

    const currentUser = useSelector((state: RootState) => selectUser(state));

    const renderTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    return (
        <>
            <Space
                direction="vertical"
                style={{
                    width: '100%',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '14px 0',
                    }}
                >
                    <Space>
                        <Search
                            placeholder="search by name, email or user group"
                            allowClear
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                            }}
                            style={{ width: 300 }}
                        />
                        <FilterTableButton<BucketMember>
                            filterableKeysAndOptions={FILTERABLE_KEYS_AND_OPTIONS}
                            onChange={(filteredUsers) => {
                                setFilteredMembers(filteredUsers);
                            }}
                            items={members}
                        />
                    </Space>
                    <Space>
                        {!isInEditMode && (
                            <PermissionedActionButton
                                icon={<PlusOutlined />}
                                type="primary"
                                onClick={() => setIsAddBucketMembersModalVisible(true)}
                                actionType={RunwayUserAction.UABucketUpdateBucketMembers}
                                bucketId={bucketId}
                            >
                                Add
                            </PermissionedActionButton>
                        )}
                        {isInEditMode ?
                            <Button type="primary" onClick={() => setIsInEditMode(false)} icon={<CheckOutlined />}>
                                Done
                            </Button>
                        :   <PermissionedActionButton
                                onClick={() => setIsInEditMode(true)}
                                icon={<EditOutlined />}
                                actionType={RunwayUserAction.UABucketUpdateBucketMembers}
                                bucketId={bucketId}
                            >
                                Edit
                            </PermissionedActionButton>
                        }
                    </Space>
                </div>

                <Table
                    dataSource={searchedMemberDetails}
                    columns={[
                        {
                            title: renderTitle('Name'),
                            key: 'name',
                            render: (member: MemberDetails) => (
                                <BuildDistroMemberRow member={member} currentUser={currentUser} />
                            ),
                        },
                        {
                            title: renderTitle('Permissions'),
                            key: 'permissions',
                            align: 'right',
                            render: (member: MemberDetails) => {
                                const isCurrentUser =
                                    member.type === BuildDistroMemberType.User && member.id === currentUser.id;

                                const shouldDisablePermissionsUpdate = isCurrentUser;

                                if (isInEditMode) {
                                    return (
                                        <EditMemberRoleSelect
                                            bucketId={bucketId}
                                            appId={appId}
                                            orgId={orgId}
                                            memberId={member.id}
                                            isDisabled={shouldDisablePermissionsUpdate}
                                            defaultPermissionLevel={member.permissions}
                                        />
                                    );
                                }

                                return (
                                    <Space>
                                        <Text
                                            style={{
                                                color: shouldDisablePermissionsUpdate ? antGrey.grey6 : antGrey.grey8,
                                                fontSize: 14,
                                            }}
                                        >
                                            {displayTextForBuildBucketPermission(member.permissions)}
                                        </Text>
                                        {shouldDisablePermissionsUpdate && (
                                            <LockOutlined
                                                style={{
                                                    color: antGrey.grey6,
                                                }}
                                            />
                                        )}
                                    </Space>
                                );
                            },
                        },
                        ...(isInEditMode ?
                            [
                                {
                                    title: renderTitle(''),
                                    key: 'remove',
                                    align: 'center',
                                    width: 50,
                                    render: (member) => {
                                        return (
                                            <RemoveMemberButton
                                                memberId={member.id}
                                                bucketId={bucketId}
                                                appId={appId}
                                                orgId={orgId}
                                                isDisabled={
                                                    member.type === BuildDistroMemberType.User &&
                                                    member.id === currentUser.id
                                                }
                                            />
                                        );
                                    },
                                } as ColumnType<MemberDetails>,
                            ]
                        :   []),
                    ]}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                />
            </Space>
            <AddBucketMembersModal
                open={isAddBucketMembersModalVisible}
                onCancel={() => setIsAddBucketMembersModalVisible(false)}
                initialMembers={[]}
                excludedMembers={members}
                appId={appId}
                orgId={orgId}
                bucketId={bucketId}
            />
        </>
    );
};

export const BucketAccessIconTooltip: React.FC<{
    bucketHasOrgWideAccess?: boolean;
    isExternalBucketView?: boolean;
    isExternalBuildView?: boolean;
}> = ({ bucketHasOrgWideAccess, isExternalBucketView, isExternalBuildView }) => {
    const tooltipText =
        isExternalBucketView ?
            'This is a public bucket. Anyone with the public link can view and install builds from this bucket.'
        : isExternalBuildView ? 'This is a public build. Anyone with the public link can view and install this build.'
        : bucketHasOrgWideAccess ?
            'This bucket has org-wide access. Anyone in your Runway org with the link can view and install builds from this bucket.'
        :   'Only bucket members can view and install builds from this bucket.';

    return (
        <Tooltip title={tooltipText}>
            {isExternalBucketView || isExternalBuildView ?
                <GlobalOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 16,
                    }}
                />
            : bucketHasOrgWideAccess ?
                <img
                    src={orgOutlined}
                    style={{
                        color: antGrey.grey6,
                    }}
                    width={16}
                    height={16}
                />
            :   <TeamOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 16,
                    }}
                />
            }
        </Tooltip>
    );
};
